import { refreshId } from "lib/graphql/api"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { useEffect, useRef, useState } from "react"
import { uniqueId } from "library/database/split-id.mjs"
import { RefiningTypes, RegisterTypeProgress } from "library/server-event-definitions/shared-events.mjs"
import { getRegisterProgress } from "routes/asset-register/plugins/asset-mapping/controller/get-register-progress"
import { useProcessingSnackbar } from "routes/asset-register/plugins/asset-mapping/plugins/progress-card/snackbar/use-processing-snackbar"
import {
    getElapsedTime,
    getEstimatedTime,
    getProcessedText,
} from "routes/asset-register/plugins/asset-mapping/plugins/progress-card/timing/progress-card-timing"

const PROGRESS_COMPLETION_DELAY_MS = 450
const activeProcessingState = new Map()

export function getActiveProcessingState(registerId) {
    return activeProcessingState.get(uniqueId(registerId)) ?? null
}

RefiningTypes.handleOnce(trackGlobalBusyState)
RegisterTypeProgress.handleOnce(trackGlobalProgress)

export function useProgressCard(target, { hasMovedOn = false } = {}) {
    const registerId = target?._id || target?.id
    const registerKey = registerId ? uniqueId(registerId) : null
    const registerName = target?.name || "this register"
    const registerProgress = getRegisterProgress.useResults(refreshId(registerId))

    const [eventBusy, setEventBusy] = useState(null)
    const [eventProgress, setEventProgress] = useState(null)
    const [isCompleting, setIsCompleting] = useState(false)
    const previousBusyRef = useRef(false)
    const localStartRef = useRef(null)

    const refresh = useRefresh(noChange)

    const globalState = registerKey ? activeProcessingState.get(registerKey) : null
    const effectiveBusy = eventBusy ?? globalState?.busy ?? null
    const busy = getBusyState(effectiveBusy, registerProgress)
    const displayBusy = busy || isCompleting

    if (displayBusy && !localStartRef.current) {
        localStartRef.current = Date.now()
    } else if (!displayBusy) {
        localStartRef.current = null
    }

    const effectiveStartedAt = localStartRef.current ?? registerProgress?.startedAt
    const totalItems = eventProgress?.total ?? globalState?.total ?? registerProgress?.total
    const remainingItems = eventProgress?.remaining ?? globalState?.remaining ?? registerProgress?.remaining
    const progress = getProgressPercentage(totalItems, remainingItems)
    const elapsedTime = getElapsedTime(effectiveStartedAt)
    const estimatedTime = getEstimatedTime({
        startedAt: effectiveStartedAt,
        total: totalItems,
        remaining: remainingItems,
    })
    const processedText = getProcessedText({
        total: totalItems,
        remaining: remainingItems,
    })
    const phaseText = getPhaseText(registerProgress?.message)
    const displayProgress = isCompleting ? 100 : progress
    const error = displayBusy ? null : registerProgress?.error ?? null

    RefiningTypes.useEvent(handleRefiningTypesUpdate, [registerKey])
    RegisterTypeProgress.useEvent(handleProgressUpdate, [registerKey])
    useProcessingSnackbar(registerId, { busy: busy && !hasMovedOn, registerName })

    useEffect(trackCompletionState, [busy])
    useEffect(refreshTimingWhileBusy, [displayBusy, refresh])

    return {
        isBusy: displayBusy,
        progress: displayProgress,
        elapsedTime,
        estimatedTime,
        processedText,
        phaseText,
        error,
        title: `Processing Asset Types for ${registerName}`,
    }

    function trackCompletionState() {
        const previousBusy = previousBusyRef.current
        previousBusyRef.current = busy

        if (busy) {
            setIsCompleting(false)
            return undefined
        }

        if (!previousBusy) {
            return undefined
        }

        setIsCompleting(true)
        setEventProgress(null)

        const timer = setTimeout(finishCompletionState, PROGRESS_COMPLETION_DELAY_MS)
        return function clearCompletionTimer() {
            clearTimeout(timer)
        }

        function finishCompletionState() {
            setIsCompleting(false)
        }
    }

    function handleRefiningTypesUpdate(eventRegisterId, isBusy) {
        if (uniqueId(eventRegisterId) === registerKey) {
            setEventBusy(!!isBusy)
        }
    }

    function handleProgressUpdate(eventRegisterId, lastBlockSize, remaining, total) {
        if (uniqueId(eventRegisterId) === registerKey) {
            setEventProgress({ total, remaining })
        }
    }

    function refreshTimingWhileBusy() {
        if (!displayBusy) return undefined

        const timer = setInterval(refresh, 1000)
        return () => clearInterval(timer)
    }
}

function isFinished(registerProgress) {
    if (!registerProgress) {
        return false
    }

    if (registerProgress.lastBlockSize < 0 || registerProgress.message === "FINISHED") {
        return true
    }

    return registerProgress.total > 0 && registerProgress.remaining <= 0
}

function isInProgress(registerProgress) {
    return !!registerProgress && !isFinished(registerProgress)
}

function getBusyState(eventBusy, registerProgress) {
    if (eventBusy === true || isInProgress(registerProgress)) {
        return true
    }

    if (eventBusy === false && isFinished(registerProgress)) {
        return false
    }

    return false
}

function getProgressPercentage(total, remaining) {
    if (!total || remaining == null) {
        return 0
    }

    return Math.max(0, Math.min(100, ((total - remaining) / total) * 100))
}

function getPhaseText(message) {
    if (!message || message === "FINISHED" || message === "Processing In Progress") {
        return null
    }

    return message
}

function trackGlobalBusyState(registerId, isBusy) {
    const key = uniqueId(registerId)
    if (isBusy) {
        const existing = activeProcessingState.get(key) || {}
        activeProcessingState.set(key, { ...existing, busy: true, startedAt: existing.startedAt ?? Date.now() })
    } else {
        activeProcessingState.delete(key)
    }
}

function trackGlobalProgress(registerId, lastBlockSize, remaining, total) {
    const key = uniqueId(registerId)
    const existing = activeProcessingState.get(key) || {}
    activeProcessingState.set(key, { ...existing, total, remaining, startedAt: existing.startedAt ?? Date.now() })
}
